<template>
  <ShopBanner v-if="ConfigShowShopBanner"/>
  <v-app id="layout-app">
    <v-app-bar density="comfortable" :color="ConfigTopbarColor" :elevation="0" class="border-b">
      <v-app-bar-nav-icon variant="text" @click.stop="drawer=!drawer"/>
      <v-spacer/>
      <HelperIcon/>
      <CopierCart/>
      <div class="ml-3 mr-3"></div>
      <Branding/>
    </v-app-bar>
    <v-navigation-drawer width="300" v-model="drawer" location="left">
      <v-card elevation="0" class="filter-drawer-card pa-4 h-100">
        <v-card-text class="pa-2">
          <v-text-field
            prepend-inner-icon="mdi-magnify"
            v-model="SearchBox"
            class="search-box mb-4"
            variant="outlined"
            bg-color="#F8F8F8"
            density="compact"
            label="Search"
            type="search"
            rounded="lg">
          </v-text-field>

          <FilterItem :hide-button="true">
            <template #title>
              <div class="d-flex justify-space-between align-center mb-3">
                <div class="filters-header-title">Filters</div>
                <div>
                  <v-btn
                    text="Reset"
                    size="x-small"
                    class="d-flex filter-reset-btn"
                    rounded="pill"
                    variant="text"
                    @click="ResetFilters"
                    v-if="HasActiveFilters"
                    prepend-icon="mdi-restart">
                  </v-btn>
                </div>
              </div>
            </template>
            <FilterTags/>
          </FilterItem>

          <v-divider/>

          <FilterItem title="Manufacturer">
            <CustomCheckbox
              :label="make.name"
              v-for="make in manufacturers.filter(man => manufacturers_allowed.includes(man.id))"
              :key="make.id"
              @CheckChange="SetAnyFilter($event,'manufacturers',+make.id)"
              :is_checked="ItemExists('manufacturers',+make.id)">
            </CustomCheckbox>
          </FilterItem>

          <v-divider/>

          <FilterItem title="Product Category">
            <CustomCheckbox
              :label="category"
              v-for="category in printer_categories"
              :key="category"
              @CheckChange="SetAnyFilter($event,'product_categories',category)"
              :is_checked="ItemExists('product_categories',category)">
            </CustomCheckbox>
          </FilterItem>

          <v-divider/>

          <FilterItem title="Intended Use">
            <CustomCheckbox
              :label="level"
              v-for="level in usage_levels"
              :key="level"
              @CheckChange="SetAnyFilter($event,'usage_levels',level)"
              :is_checked="ItemExists('usage_levels',level)">
            </CustomCheckbox>
          </FilterItem>

          <v-divider/>

          <FilterItem title="Mono or Color">
            <CustomCheckbox label="Color" @CheckChange="SetAnyFilter($event,'copier_colors','Color')" :is_checked="ItemExists('copier_colors','Color')"/>
            <CustomCheckbox label="Mono" @CheckChange="SetAnyFilter($event,'copier_colors','B&W')" :is_checked="ItemExists('copier_colors','B&W')"/>
          </FilterItem>

          <v-divider/>

          <FilterItem title="Form Factor">
            <CustomCheckbox
              :label="form_factor"
              v-for="form_factor in form_factors"
              :key="form_factor"
              @CheckChange="SetAnyFilter($event,'form_factors',form_factor)"
              :is_checked="ItemExists('form_factors',form_factor)">
            </CustomCheckbox>
          </FilterItem>

          <v-divider/>

          <FilterItem title="Capabilities">
            <CustomCheckbox
              :label="capability"
              v-for="capability in capabilities"
              :key="capability"
              @CheckChange="SetAnyFilter($event,'capabilities',capability)"
              :is_checked="ItemExists('capabilities',capability)">
            </CustomCheckbox>
          </FilterItem>

          <v-divider/>

          <FilterItem title="Connectivity">
            <CustomCheckbox
              :label="connection"
              v-for="connection in connection_types"
              :key="connection"
              @CheckChange="SetAnyFilter($event,'connection_types',connection)"
              :is_checked="ItemExists('connection_types',connection)">
            </CustomCheckbox>
          </FilterItem>

          <v-divider/>

          <FilterItem title="Speed (PPM)">
            <v-row>
              <v-col cols="12" lg="6" md="6" sm="12">
                <v-text-field @input="SetSpeedMin" :model-value="filters.speed_minimum" rounded="lg" type="number" variant="outlined" density="compact" label="Min" placeholder="min"/>
              </v-col>
              <v-col cols="12" lg="6" md="6" sm="12">
                <v-text-field @input="SetSpeedMax" :model-value="filters.speed_maximum" rounded="lg" type="number" variant="outlined" density="compact" label="Max" placeholder="max"/>
              </v-col>
            </v-row>
          </FilterItem>

          <v-divider/>

          <FilterItem title="Paper Size">
            <CustomCheckbox
              :label="paper_size"
              v-for="paper_size in paper_sizes"
              :key="paper_size"
              :is_checked="ItemExists('paper_sizes',paper_size)"
              @CheckChange="SetAnyFilter($event,'paper_sizes',paper_size)">
            </CustomCheckbox>
          </FilterItem>
        </v-card-text>
      </v-card>
    </v-navigation-drawer>
    <v-main>
      <v-card-text class="pa-1 pa-sm-3 pa-md-5" style="background:#e8e8e8">
        <slot name="content"/>
      </v-card-text>
      <div class="d-flex" v-if="$slots.footer" style="background:#f1f1f1" elevation="1">
        <slot name="footer"/>
      </div>
    </v-main>
  </v-app>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { storeToRefs } from 'pinia'
import { useGlobalsStore } from '@/stores/globals'

// Imported Components
import CopierCart from "@/components/configurator/CopierCart.vue"
import HelperIcon from "@/components/configurator/HelperIcon.vue"
import CustomCheckbox from "@/components/typography/CustomCheckbox.vue"
import FilterTags from "@/components/catalog/FilterTags.vue"
import FilterItem from "@/components/catalog/FilterItem.vue"
import Branding from "@/components/misc/Branding.vue"
import ShopBanner from "@/components/catalog/ShopBanner.vue"

// Global Store
const store = useGlobalsStore()
const {
  filters,
  manufacturers,
  manufacturers_allowed,
  printer_categories,
  paper_sizes,
  usage_levels,
  form_factors,
  capabilities,
  connection_types,
  ConfigShowShopBanner,
  ConfigTopbarColor,
  config_drawer,
  is_mobile
} = storeToRefs(store)

// Component State
const drawer = ref(true)
const is_logged_in = ref(false)
const profile = ref<any>(null)
const controls = ref({
  brands: false,
})

// Methods
function SetSpeedMin(event: any) {
  const filtersCopy = { ...filters.value }
  filtersCopy.speed_minimum = event.target.value ? Number(event.target.value) : null
  store.SetFilters(filtersCopy)
}

function SetSpeedMax(event: any) {
  const filtersCopy = { ...filters.value }
  filtersCopy.speed_maximum = event.target.value ? Number(event.target.value) : null
  store.SetFilters(filtersCopy)
}

function SetAnyFilter(is_checked: any, target: string, value: any) {
  if (typeof is_checked === 'object' && is_checked !== null && 'target' in is_checked) {
    is_checked = is_checked.target.checked
  }
  const rawFilters = { ...filters.value } as any
  const exists = rawFilters[target].find((c: any) => c == value)

  if (is_checked == true) {
    if (!exists) {
      rawFilters[target].push(value)
    }
  } else {
    if (exists) {
      const index = rawFilters[target].indexOf(value)
      if (index !== -1) {
        rawFilters[target].splice(index, 1)
      }
    }
  }
  store.SetFilters(rawFilters)
}

function ItemExists(target: string, value: any) {
  const filterList = (filters.value as any)[target] as any[]
  return filterList.includes(value)
}

function ResetFilters() {
  filters.value = {
    sort_type: null,
    page_limit: 12,
    search_input: '',
    manufacturers: [],
    device_types: [],
    copier_colors: [],
    speed_minimum: null,
    speed_maximum: null,
    price_filters: [],
    price_options: [
      { label: "$69-$99", min: 69, max: 99 },
      { label: "$100-$199", min: 100, max: 199 },
      { label: "$200+", min: 200, max: 9999 }
    ],
    paper_sizes: [],
    product_categories: [],
    form_factors: [],
    capabilities: [],
    connection_types: [],
    usage_levels: [],
  }
}

// Computed
const SearchBox = computed({
  get() {
    return filters.value.search_input
  },
  set(value) {
    store.SetFilters({
      ...filters.value,
      search_input: value
    })
  }
})

const HasActiveFilters = computed(() => {
  const { manufacturers, device_types, copier_colors, price_filters, paper_sizes, product_categories, form_factors, capabilities, connection_types, usage_levels, search_input, speed_minimum, speed_maximum } = filters.value
  return [manufacturers, device_types, copier_colors, price_filters, paper_sizes, product_categories, form_factors, capabilities, connection_types, usage_levels].some(f => f.length > 0) || !!search_input || !!speed_minimum || !!speed_maximum
})

onMounted(() => {
  const isMobile = window.innerWidth <= 768
  if (isMobile) {
    drawer.value = false
    config_drawer.value = false
    is_mobile.value = isMobile
  }
})
</script>
<style lang="scss">
#layout-app {
  .v-application__wrap {
    border-bottom: none !important;
    height: fit-content !important;
    margin-bottom: 0;
    min-height: 100vh;
    background: #f8fafc;
  }
}

/* Custom Scrollbar for Sidebar Drawer */
.v-navigation-drawer__content {
  scrollbar-width: thin;
  scrollbar-color: #e2e8f0 transparent;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background-color: #e2e8f0;
    border-radius: 20px;
    border: transparent;
    transition: background-color 0.2s ease;

    &:hover {
      background-color: #cbd5e1;
    }
  }
}

/* Sidebar Drawer Card Redesign */
.filter-drawer-card {
  font-family: 'Inter', sans-serif;
  background: #ffffff !important;
  border-right: 1px solid #f1f5f9;

  .v-card-text {
    padding: 8px 12px !important;
  }

  .v-divider {
    border-color: #f1f5f9 !important;
    opacity: 0.8;
    margin: 10px 0 !important;
  }
}

/* Header Filters Title & Reset Button */
.filters-header-title {
  font-family: 'Outfit', sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: #0f172a;
  letter-spacing: -0.01em;
}

.filter-reset-btn {
  text-transform: none !important;
  font-size: 11px !important;
  font-weight: 600 !important;
  color: #64748b !important;
  padding: 0 8px !important;
  height: 24px !important;
  transition: all 0.2s ease;

  &:hover {
    color: #ef4444 !important;
    background-color: rgba(239, 68, 68, 0.08) !important;
  }
}

/* App Bar styling */
#layout-app .v-app-bar.border-b {
  border-bottom: 1px solid #f1f5f9 !important;
  backdrop-filter: blur(12px);
  background: rgba(255, 255, 255, 0.85) !important;
}

/* Search Box & Range Input border overrides */
.search-box, .filter-drawer-card .v-text-field {
  .v-field {
    border-radius: 10px !important;
    background-color: #f8fafc !important;
    transition: all 0.2s ease-in-out !important;
    border: 1px solid #e2e8f0 !important;
    box-shadow: none !important;

    .v-field__outline {
      display: none !important;
    }

    &:hover {
      background-color: #f1f5f9 !important;
      border-color: #cbd5e1 !important;
    }

    &.v-field--focused {
      background-color: #ffffff !important;
      border-color: #3b82f6 !important;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.12) !important;
    }
  }

  .v-input__details {
    display: none !important;
  }
}
</style>
